import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './AdminShared.module.scss';
import SearchDropdown from './components/SearchDropdown';
import {
    ParticipantAttributeTypeDto,
    ParticipantAttributeValueCreateDto,
    ParticipantDto
} from '../../../types/admin.types';
import {
    fetchParticipantAttributeTypes,
    fetchParticipantAttributeValue,
    fetchParticipants,
    setParticipantAttributeValue
} from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';

const AdminParticipantAttributeValues = () => {
    const { getUserInfo } = useUserState();
    const token = getUserInfo()?.token;
    const [participants, setParticipants] = useState<ParticipantDto[]>([]);
    const [attributeTypes, setAttributeTypes] = useState<ParticipantAttributeTypeDto[]>([]);
    const [selectedParticipantId, setSelectedParticipantId] = useState('');
    const [selectedAttributeTypeId, setSelectedAttributeTypeId] = useState('');
    const [value, setValue] = useState('');
    const [isDirty, setIsDirty] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingValue, setIsLoadingValue] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const loadBaseData = useCallback(async () => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            const [participantsResponse, typesResponse] = await Promise.all([
                fetchParticipants(token),
                fetchParticipantAttributeTypes(token)
            ]);
            const orderedParticipants = [...participantsResponse].sort((a, b) => a.Name.localeCompare(b.Name));
            const orderedTypes = [...typesResponse].sort((a, b) => a.Name.localeCompare(b.Name));
            setParticipants(orderedParticipants);
            setAttributeTypes(orderedTypes);
            if (orderedParticipants.length > 0) {
                setSelectedParticipantId((current) => current || orderedParticipants[0].Id);
            }
            if (orderedTypes.length > 0) {
                setSelectedAttributeTypeId((current) => current || orderedTypes[0].Id);
            }
        } catch (error) {
            setErrorMessage('Unable to load participant attributes.');
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    const loadValue = useCallback(async (participantId: string, attributeTypeId: string) => {
        setIsLoadingValue(true);
        setErrorMessage(null);
        setSuccessMessage(null);
        try {
            const response = await fetchParticipantAttributeValue(participantId, attributeTypeId, token);
            setValue(response?.Value ?? '');
            setIsDirty(false);
        } catch (error) {
            setErrorMessage('Unable to load attribute value.');
        } finally {
            setIsLoadingValue(false);
        }
    }, [token]);

    useEffect(() => {
        loadBaseData();
    }, [loadBaseData]);

    useEffect(() => {
        if (!selectedParticipantId || !selectedAttributeTypeId) {
            setValue('');
            setIsDirty(false);
            return;
        }
        loadValue(selectedParticipantId, selectedAttributeTypeId);
    }, [selectedParticipantId, selectedAttributeTypeId, loadValue]);

    const handleSave = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!selectedParticipantId || !selectedAttributeTypeId) {
            setErrorMessage('Select a participant and attribute type first.');
            return;
        }

        const payload: ParticipantAttributeValueCreateDto = {
            ParticipantId: selectedParticipantId,
            TypeId: selectedAttributeTypeId,
            Value: value.trim().length > 0 ? value : null
        };

        setErrorMessage(null);
        setSuccessMessage(null);
        try {
            await setParticipantAttributeValue(payload, token);
            setSuccessMessage('Attribute saved.');
            setIsDirty(false);
        } catch (error) {
            setErrorMessage('Unable to save attribute value.');
        }
    };

    const participantLabel = (participant: ParticipantDto) =>
        participant.Nickname ? `${participant.Name} (${participant.Nickname})` : participant.Name;

    const participantOptions = useMemo(
        () => participants.map((participant) => ({
            value: participant.Id,
            label: participantLabel(participant)
        })),
        [participants]
    );

    const attributeTypeOptions = useMemo(
        () => attributeTypes.map((type) => ({
            value: type.Id,
            label: type.Name
        })),
        [attributeTypes]
    );

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div>
                    <h1>Participant Attribute Values</h1>
                    <p>Set individual attribute values for fighters.</p>
                </div>
            </header>

            {errorMessage ? <div className={styles.muted}>{errorMessage}</div> : null}
            {successMessage ? <div className={styles.muted}>{successMessage}</div> : null}

            <div className={styles.content}>
                <section className={styles.panel}>
                    <h2>Selection</h2>
                    {isLoading ? (
                        <div className={styles.muted}>Loading participants...</div>
                    ) : participants.length === 0 ? (
                        <div className={styles.empty}>No participants available.</div>
                    ) : (
                        <div>
                            <div className={styles.field}>
                                <label htmlFor="attribute-participant">Participant</label>
                                <SearchDropdown
                                    id="attribute-participant"
                                    value={selectedParticipantId}
                                    options={participantOptions}
                                    onChange={(value) => setSelectedParticipantId(value ?? '')}
                                    placeholder="Search participant"
                                    noResultsText="No participants found."
                                />
                            </div>
                            <div className={styles.field}>
                                <label htmlFor="attribute-type">Attribute Type</label>
                                <SearchDropdown
                                    id="attribute-type"
                                    value={selectedAttributeTypeId}
                                    options={attributeTypeOptions}
                                    onChange={(value) => setSelectedAttributeTypeId(value ?? '')}
                                    placeholder="Search attribute type"
                                    noResultsText="No attribute types found."
                                    clearable={false}
                                />
                            </div>
                        </div>
                    )}
                </section>

                <section className={styles.panel}>
                    <h2>Value</h2>
                    {selectedParticipantId && selectedAttributeTypeId ? (
                        <form onSubmit={handleSave}>
                            <div className={styles.field}>
                                <label htmlFor="attribute-value">Attribute Value</label>
                                <textarea
                                    id="attribute-value"
                                    value={value}
                                    onChange={(event) => {
                                        setValue(event.target.value);
                                        setIsDirty(true);
                                    }}
                                    maxLength={200}
                                    disabled={isLoadingValue}
                                />
                            </div>
                            <div className={styles.actions}>
                                <button className={styles.primary} type="submit" disabled={!isDirty}>
                                    {isLoadingValue ? 'Loading...' : 'Save Value'}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <div className={styles.muted}>Select a participant and attribute type to edit values.</div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default AdminParticipantAttributeValues;
